// ============================================================
//  WORKER DIRECTORY PAGE — ADMIN DASHBOARD
//  Path: src/pages/WorkerDirectoryPage.tsx
//
//  Admin oversight of individuals who work FOR a company, via the
//  CompanyWorker roster — filterable by the worker's own address
//  (country / province / city), across every company on the
//  platform. This is distinct from CompaniesPage's per-company
//  roster list (that shows one company's workers at a time) and
//  from GeographyPage (which covers every expert/customer,
//  regardless of whether they're on a company roster). Backed by
//  GET /api/admin/company-workers — admin.service.js's
//  getCompanyWorkerDirectory.
//
//  FIX (review pass):
//  - Responses could arrive out of order (fast Prev/Next or filter
//    changes) and an older page overwrote the newer one; stale
//    responses are ignored now.
//  - A failed load showed "0 workers found" above the error, with no
//    way to retry. The count is hidden on error and there's Try again.
//  - If the list shrank (e.g. a worker removed) while on the last page,
//    the page said "No roster workers yet"; it now steps back a page.
//  - Typed-but-not-applied filters were easy to miss; the page says so
//    and Clear appears as soon as anything is typed.
//  - "invited" workers haven't accepted yet, so they're labelled that
//    way instead of looking like they work for the company.
//  - Backend (admin.service.js): filters now match like the Geography
//    page — "Ontario" also finds "ON", "Toronto" finds "Toronto ".
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listCompanyWorkerDirectory } from '../services/admin.service'

interface WorkerRow {
  companyWorkerId: string
  status: 'invited' | 'accepted' | 'declined' | 'removed'
  invitedAt: string
  respondedAt: string | null
  workerExpertId: string
  workerUserId: string | null
  workerName: string
  workerEmail: string | null
  workerPhone: string | null
  workerAvatar: string | null
  backgroundCheckPassed: boolean
  totalJobsCompleted: number
  city: string | null
  province: string | null
  country: string | null
  companyExpertId: string
  companyName: string
}

const STATUS_COLORS: Record<string, string> = {
  accepted: 'bg-green-500/20 text-green-400',
  invited:  'bg-yellow-500/20 text-yellow-400',
}

const STATUS_LABELS: Record<string, string> = {
  accepted: 'Accepted',
  invited:  'Invited — not accepted yet',
}

const loadErrorMessage = (err: any): string =>
  err?.response?.data?.message ||
  (err?.isNetworkError || !err?.response
    ? 'Could not reach the server — check your connection and try again.'
    : 'Failed to load the worker directory.')

const Avatar = ({ name }: { name: string }) => (
  <div className="w-9 h-9 rounded-full bg-brand-700 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
    {name?.[0]?.toUpperCase() ?? '?'}
  </div>
)

const WorkerDirectoryPage: React.FC = () => {
  const navigate = useNavigate()

  const [workers, setWorkers]     = useState<WorkerRow[]>([])
  const [total, setTotal]         = useState(0)
  const [pageSize, setPageSize]   = useState(20)
  const [page, setPage]           = useState(1)
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState('')

  // Filter inputs are free-text (matched case-insensitively on the
  // backend), separate from the applied filters below — an admin
  // typing "Tor" shouldn't refetch on every keystroke.
  const [countryInput, setCountryInput]   = useState('')
  const [provinceInput, setProvinceInput] = useState('')
  const [cityInput, setCityInput]         = useState('')
  const [appliedFilters, setAppliedFilters] = useState({ country: '', province: '', city: '' })

  // Only the newest request may update the page.
  const requestSeq = useRef(0)

  const fetchWorkers = useCallback(async () => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError('')
    try {
      const res: any = await listCompanyWorkerDirectory({
        country:  appliedFilters.country  || undefined,
        province: appliedFilters.province || undefined,
        city:     appliedFilters.city     || undefined,
        page,
        limit: 20,
      })
      if (seq !== requestSeq.current) return
      const data = res?.data?.data
      const rows: WorkerRow[] = data?.workers ?? []
      const newTotal: number = data?.total ?? 0
      const newPageSize: number = data?.pageSize || 20
      // Past the last page (list shrank) — go back instead of showing
      // an empty page that reads as "no workers".
      const lastPage = Math.max(1, Math.ceil(newTotal / newPageSize))
      if (rows.length === 0 && page > lastPage) {
        setPage(lastPage)
        return
      }
      setWorkers(rows)
      setTotal(newTotal)
      setPageSize(newPageSize)
    } catch (err: any) {
      if (seq !== requestSeq.current) return
      console.error('Failed to load worker directory:', err)
      setWorkers([])
      setTotal(0)
      setError(loadErrorMessage(err))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [appliedFilters, page])

  useEffect(() => { fetchWorkers() }, [fetchWorkers])

  const applyFilters = () => {
    setPage(1)
    setAppliedFilters({ country: countryInput.trim(), province: provinceInput.trim(), city: cityInput.trim() })
  }

  const clearFilters = () => {
    setCountryInput(''); setProvinceInput(''); setCityInput('')
    setPage(1)
    setAppliedFilters({ country: '', province: '', city: '' })
  }

  const hasActiveFilters = !!(appliedFilters.country || appliedFilters.province || appliedFilters.city)
  const hasTypedFilters  = !!(countryInput.trim() || provinceInput.trim() || cityInput.trim())
  const filtersUnapplied =
    countryInput.trim()  !== appliedFilters.country ||
    provinceInput.trim() !== appliedFilters.province ||
    cityInput.trim()     !== appliedFilters.city
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Worker Directory</h1>
        <p className="text-gray-400 text-sm mt-1">
          Individuals working for a company, across every company on the platform — filter by their own location.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-surface-card border border-border-light rounded-2xl p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Country</label>
            <input
              type="text" value={countryInput} onChange={(e) => setCountryInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              placeholder="e.g. Canada"
              className="w-full text-sm bg-surface-tertiary border border-border-light rounded-lg p-2 text-white placeholder-gray-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Province / State</label>
            <input
              type="text" value={provinceInput} onChange={(e) => setProvinceInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              placeholder="e.g. Ontario"
              className="w-full text-sm bg-surface-tertiary border border-border-light rounded-lg p-2 text-white placeholder-gray-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">City</label>
            <input
              type="text" value={cityInput} onChange={(e) => setCityInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              placeholder="e.g. Toronto"
              className="w-full text-sm bg-surface-tertiary border border-border-light rounded-lg p-2 text-white placeholder-gray-500"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button onClick={applyFilters}
            className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 transition-colors">
            Apply Filters
          </button>
          {(hasActiveFilters || hasTypedFilters) && (
            <button onClick={clearFilters}
              className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">
              Clear
            </button>
          )}
          {filtersUnapplied && (
            <span className="text-xs text-yellow-400">Filters changed — press Apply (or Enter) to update the list.</span>
          )}
        </div>
        <p className="text-xs text-gray-600 mt-2">
          Full names only, e.g. "Toronto" (not "Tor"). Province can be the name or code ("Ontario" or "ON").
        </p>
      </div>

      {!loading && !error && (
        <p className="text-gray-400 text-sm mb-3">{total} worker{total !== 1 ? 's' : ''} found</p>
      )}

      {loading ? (
        <div className="bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">Loading...</div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={() => fetchWorkers()}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      ) : workers.length === 0 ? (
        <div className="bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">
          {hasActiveFilters ? 'No roster workers match those filters.' : 'No roster workers yet.'}
        </div>
      ) : (
        <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
          <div className="divide-y divide-border-light">
            {workers.map((w) => (
              <div key={w.companyWorkerId} className="flex items-center gap-4 px-5 py-4 hover:bg-surface-tertiary/30 transition-colors">
                <Avatar name={w.workerName} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-white">{w.workerName || 'Unnamed'}</p>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[w.status] ?? 'bg-gray-500/20 text-gray-400'}`}>
                      {STATUS_LABELS[w.status] ?? w.status}
                    </span>
                    {w.backgroundCheckPassed ? (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">✅ Background checked</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-500">Not background checked</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{w.workerEmail || '—'} · {w.workerPhone || '—'}</p>
                  <p className="text-xs text-gray-600 mt-0.5 truncate">
                    🏢 {w.companyName || 'Unnamed company'} · 📍 {[w.city, w.province, w.country].filter(Boolean).join(', ') || 'No address on file'}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-gray-400">{w.totalJobsCompleted} job{w.totalJobsCompleted !== 1 ? 's' : ''} completed</p>
                  <button onClick={() => navigate(`/experts/${w.workerExpertId}`)}
                    className="mt-1.5 px-3 py-1 text-xs bg-brand-500/15 text-brand-400 rounded-lg hover:bg-brand-500/25 transition-colors">
                    View Profile
                  </button>
                </div>
              </div>
            ))}
          </div>
          {total > pageSize && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border-light">
              <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">
                  Prev
                </button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default WorkerDirectoryPage